import {
  ClipboardList,
  FileInput,
  FileSignature,
  FlaskConical,
  Handshake,
  History,
  MessagesSquare,
  SlidersHorizontal,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Todo o texto da landing em um arquivo só.
 *
 * Copy de marketing muda muito mais rápido que layout, e quem revisa o texto
 * (marca, não dev) não deveria precisar abrir JSX para trocar uma frase.
 *
 * Regra dura deste arquivo: **nada aqui pode prometer o que o produto não
 * entrega.** Cada item abaixo corresponde a um domínio do escopo do MVP
 * (`docs-migração/PLANO-MVP.md §4`). Número de usuário, tempo de economia e
 * preço ficam de fora — não temos nenhum dos três medido, e preço ainda é
 * decisão pendente (DEC-1).
 */

export const MARCA = {
  nome: 'NOHRA Clinic',
  /** Meta description do site atual, mantida palavra por palavra. */
  tagline: 'Plataforma de apoio à prática clínica veterinária, com base científica do Hormonalle.',
  grupo: 'Grupo Pesquisas Hormonais',
} as const;

/** Marcas irmãs, na ordem em que aparecem no documento de identidade visual. */
export const ECOSSISTEMA: readonly string[] = [
  'Pesquisas Hormonais',
  'Hormonalle',
  'Hormone Lovers',
  'Além do hormônio',
];

/** Âncoras da nav. O `id` é o mesmo usado na `<section>` correspondente. */
export const ANCORAS = [
  { id: 'recursos', rotulo: 'Recursos' },
  { id: 'analise-comparativa', rotulo: 'Análise comparativa' },
  { id: 'como-funciona', rotulo: 'Como funciona' },
  { id: 'assessoria', rotulo: 'Assessoria' },
  { id: 'clube', rotulo: 'Clube' },
] as const;

/**
 * Tela cheia própria, logo após Recursos — a leitura sai do paciente
 * individual e vai para o conjunto da rotina (grupo, sexo, exame, idade).
 */
export const ANALISE_COMPARATIVA = {
  selo: 'Análise comparativa',
  titulo: 'A leitura que vai além de cada paciente.',
  paragrafo:
    'Selecione pacientes por grupo, sexo, exame ou idade e enxergue a sua rotina como um todo — para identificar tendências, comparar tratamentos e reunir a base para os seus resumos científicos.',
  card: {
    icone: SlidersHorizontal,
    titulo: 'Filtre do seu jeito',
    descricao:
      'Reúna em poucos cliques os dados que sustentam um resumo científico, um pôster ou uma apresentação a partir da sua própria rotina.',
  },
} as const;

export interface Recurso {
  icone: LucideIcon;
  titulo: string;
  descricao: string;
}

export const RECURSOS: readonly Recurso[] = [
  {
    icone: Users,
    titulo: 'Pacientes e responsáveis',
    descricao:
      'Dados do paciente, responsável, contatos e informações clínicas organizados em um só lugar, com busca simples e acesso rápido.',
  },
  {
    icone: ClipboardList,
    titulo: 'Prontuário clínico organizado',
    descricao:
      'Registre cada atendimento e acompanhe a evolução do paciente sem perder informações anteriores.',
  },
  {
    icone: FlaskConical,
    titulo: 'Exames e resultados',
    descricao:
      'Visualize resultados de exames e intervalos de referência de forma clara, sempre vinculados ao histórico do paciente.',
  },
  {
    icone: FileInput,
    titulo: 'Solicitação de exames',
    descricao:
      'Selecione os exames no portfólio, gere a solicitação e compartilhe com o responsável de forma rápida e prática.',
  },
  {
    icone: FileSignature,
    titulo: 'Receituários',
    descricao:
      'Crie prescrições com mais agilidade, reutilize seus modelos e entregue o documento pronto ao responsável. Acesse também produtos dos parceiros diretamente durante a prescrição.',
  },
  {
    icone: History,
    titulo: 'Histórico e evolução',
    descricao:
      'Acompanhe resultados, tratamentos e mudanças clínicas ao longo do tempo. Tudo na mesma linha de cuidado, para perceber com mais clareza a evolução do seu paciente.',
  },
  {
    icone: MessagesSquare,
    titulo: 'Discussões técnicas',
    descricao:
      'Abra uma discussão sobre o caso e converse com a assessoria científica, com o histórico do paciente ao lado.',
  },
  {
    icone: Ticket,
    titulo: 'Clube Hormonalle',
    descricao: 'Seu relacionamento com o Hormonalle gera benefícios, vantagens exclusivas.',
  },
  {
    icone: Handshake,
    titulo: 'PARCEIROS',
    descricao:
      'Acesse soluções, produtos e conteúdos técnicos selecionados para apoiar diferentes momentos da sua rotina clínica.',
  },
];

export interface Passo {
  numero: string;
  titulo: string;
  descricao: string;
}

export const PASSOS: readonly Passo[] = [
  {
    numero: '01',
    titulo: 'Crie sua conta',
    descricao: 'Cadastre-se com seu CRMV e acesse a NOHRA pelo navegador ou aplicativo.',
  },
  {
    numero: '02',
    titulo: 'Cadastre o paciente',
    descricao: 'Inclua os dados essenciais uma única vez. A partir daí, exames, prescrições, histórico e discussões permanecem conectados ao mesmo paciente.',
  },
  {
    numero: '03',
    titulo: 'Solicite ou lance exames',
    descricao:
      'Crie novas solicitações pelo portfólio. Os resultados integrados entram automaticamente, e exames externos também podem fazer parte do histórico.',
  },
  {
    numero: '04',
    titulo: 'Acompanhe a evolução',
    descricao:
      'Compare resultados e consulte todo o histórico do paciente em uma única visão.',
  },
  {
    numero: '05',
    titulo: 'Prescreva com praticidade',
    descricao:
      'Use seus modelos, gere o receituário e compartilhe o documento com o responsável.',
  },
  {
    numero: '06',
    titulo: 'Abra uma discussão',
    descricao:
      'Abra uma discussão com um especialista sem reunir novamente todas as informações do caso.',
  },
  {
    numero: '07',
    titulo: 'Leia a evolução e decida',
    descricao:
      'Quanto mais a jornada avança, mais completa se torna a visão do paciente. E quanto mais pacientes você acompanha, mais clara fica a leitura da sua rotina como um todo.',
  },
];
